import { useSelector } from 'react-redux';
import type { RootState } from '../../store/store';
import { useCan } from '../rbac';
import {
  selectIsPlanLocked,
  selectPlanLockEndedPlanKind,
  selectPlanLockFollowsTrial,
} from './plan-lock-rules';
import type { PlanKind } from './plan-lock-rules';

export interface PlanLock {
  /** The organisation has no active plan or trial and central-api enforces it. */
  locked: boolean;
  /**
   * The signed-in member can buy a plan: they hold `billing.manage` (the
   * capability the subscription page asks for before it shows the plans) and
   * a seller session, which the checkout calls run on.
   */
  canManageBilling: boolean;
  /**
   * The member holds `billing.manage` but signed in without a seller session
   * (an invited co-owner). They cannot check out from this session: a plan is
   * bought from the account that registered the organisation.
   */
  needsAccountHolderSignIn: boolean;
  /** The lock follows a trial that ended (as opposed to "never had a plan"). */
  trialEnded: boolean;
  /**
   * The plan the lock follows: a trial or a paid plan that ended, or `null`
   * when there was none (or it is not known). Always `null` when not locked.
   */
  endedPlanKind: PlanKind | null;
}

export function usePlanLock(): PlanLock {
  const locked = useSelector(selectIsPlanLocked);
  const trialEnded = useSelector(selectPlanLockFollowsTrial);
  const endedPlanKind = useSelector(selectPlanLockEndedPlanKind);
  const hasSellerSession = useSelector(
    (state: RootState) => !!state.auth?.authorization?.accessToken?.sellerToken
  );
  const mayManageBilling = useCan('billing.manage');

  return {
    locked,
    canManageBilling: mayManageBilling && hasSellerSession,
    needsAccountHolderSignIn: mayManageBilling && !hasSellerSession,
    trialEnded: locked && trialEnded,
    endedPlanKind: locked ? endedPlanKind : null,
  };
}
